import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Form, Input, Space, message, ColorPicker, Typography, Popconfirm, Badge, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import useCategoryStore from '../../store/useCategoryStore';

const { Text } = Typography;

const CategoryManager = ({ visible, onCancel, onRefreshNeeded }) => {
    const { categories, fetchCategories } = useCategoryStore();
    const [loading, setLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [form] = Form.useForm();
    const [usageCounts, setUsageCounts] = useState({});
    const [searchText, setSearchText] = useState('');

    const fetchCounts = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const counts = {};
            await Promise.all(categories.map(async (c) => {
                try {
                    const usageRes = await axios.get(`/api/categories/${c.id}/usage`, { headers: { Authorization: `Bearer ${token}` } });
                    counts[c.id] = usageRes.data.count;
                } catch (e) { counts[c.id] = 0; }
            }));
            setUsageCounts(counts);
        } catch (error) {
            console.error('Failed to load usage counts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            fetchCategories();
        }
    }, [visible, fetchCategories]);

    useEffect(() => {
        if (categories.length > 0) {
            fetchCounts();
        }
    }, [categories]);

    const handleAdd = () => {
        setEditingCategory(null);
        form.resetFields();
        form.setFieldsValue({ color: '#1890ff' });
        setEditModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingCategory(record);
        form.setFieldsValue({
            label: record.label,
            color: record.color || '#1890ff',
            description: record.description
        });
        setEditModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/categories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            message.success('Category deleted');
            fetchCategories();
            if (onRefreshNeeded) onRefreshNeeded();
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Failed to delete category';
            message.error(errorMsg);
        }
    };

    const handleSave = async (values) => {
        const payload = {
            ...values,
            color: typeof values.color === 'string' ? values.color : values.color.toHexString()
        };

        // Client-side duplicate check
        const isDuplicate = categories.some(c =>
            c.label.toLowerCase() === payload.label.toLowerCase() &&
            (!editingCategory || c.id !== editingCategory.id)
        );
        if (isDuplicate) {
            return message.error('A category with this name already exists');
        }

        try {
            const token = localStorage.getItem('token');
            if (editingCategory) {
                await axios.put(`/api/categories/${editingCategory.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                message.success('Category updated');
            } else {
                await axios.post('/api/categories', payload, { headers: { Authorization: `Bearer ${token}` } });
                message.success('Category created');
            }
            setEditModalVisible(false);
            fetchCategories();
            if (onRefreshNeeded) onRefreshNeeded();
        } catch (error) {
            message.error(error.response?.data?.error || 'Operation failed');
        }
    };

    const filteredCategories = categories.filter(c =>
        (c.label || '').toLowerCase().includes(searchText.toLowerCase())
    );

    const columns = [
        {
            title: 'Name',
            dataIndex: 'label',
            key: 'label',
            render: (text, record) => (
                <Space>
                    <Badge color={record.color || '#1890ff'} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Usage',
            key: 'usage',
            width: 100,
            align: 'center',
            render: (_, record) => (
                <Tooltip title={`${usageCounts[record.id] || 0} products use this category`}>
                    <Badge count={usageCounts[record.id] || 0} showZero color="#faad14" />
                </Tooltip>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
                    <Popconfirm
                        title="Delete category?"
                        description={usageCounts[record.id] > 0 ? "Items are linked to this category." : "Are you sure?"}
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Modal
            title="Manage Product Categories"
            open={visible}
            onCancel={onCancel}
            width={800}
            footer={[
                <Button key="close" onClick={onCancel}>Close</Button>,
                <Button key="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>New Category</Button>
            ]}
        >
            <div style={{ marginBottom: 16 }}>
                <Input
                    placeholder="Search categories..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                />
            </div>

            <Table
                dataSource={filteredCategories}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                size="small"
                scroll={{ y: 400 }}
                locale={{ emptyText: categories.length === 0 ? 'No categories found. Create your first one!' : 'No results matching search' }}
            />

            <Modal
                title={editingCategory ? 'Edit Category' : 'New Category'}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={() => form.submit()}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSave}>
                    <Form.Item name="label" label="Category Name" rules={[{ required: true, message: 'Please enter a name' }]}>
                        <Input placeholder="e.g. Smartphones, Accessories" maxLength={50} />
                    </Form.Item>
                    <Form.Item name="color" label="Display Color">
                        <ColorPicker showText />
                    </Form.Item>
                    <Form.Item name="description" label="Description (Optional)">
                        <Input.TextArea placeholder="Internal notes" rows={2} maxLength={255} />
                    </Form.Item>
                </Form>
            </Modal>
        </Modal>
    );
};

export default CategoryManager;
